// src/components/admin/products/tabs/FreeQuestionsAdmin.tsx

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Search, Save, ToggleLeft, ToggleRight,
    CheckSquare, Square, ChevronDown, Lock,
    RefreshCw, Info, X
} from 'lucide-react';
import './FreeQuestionsAdmin.css';

interface FreeQuestionsAdminProps {
    onShowToast: (msg: string, type: 'success' | 'error') => void;
}

interface QuestionItem {
    _id: string;
    statement: string;
    difficulty: 'facil' | 'medio' | 'dificil';
    category: {
        level1?: string;
        level2?: string;
        level3?: string;
        level4?: string;
    };
}

interface CategoryNode {
    _id: string;
    name: string;
    level: number;
    questionsCount: number;
    children: CategoryNode[];
}

interface FreeConfig {
    enabled: boolean;
    dailyLimit: number;
    totalLimit: number;
    freeQuestionIds: string[];
}

function flattenTree(nodes: CategoryNode[], parentId: string | null = null): Array<{
    _id: string; name: string; level: number; parentId: string | null;
}> {
    const result: any[] = [];
    for (const node of nodes) {
        result.push({ _id: node._id, name: node.name, level: node.level, parentId });
        if (node.children?.length) result.push(...flattenTree(node.children, node._id));
    }
    return result;
}

const DIFF_LABEL: Record<string, string> = { facil: 'Fácil', medio: 'Médio', dificil: 'Difícil' };
const DIFF_COLOR: Record<string, string> = { facil: '#003BD1', medio: '#D4B35D', dificil: '#EF4444' };

const FreeQuestionsAdmin: React.FC<FreeQuestionsAdminProps> = ({ onShowToast }) => {
    const [config, setConfig] = useState<FreeConfig>({
        enabled: false, dailyLimit: 0, totalLimit: 0, freeQuestionIds: []
    });
    const [questions, setQuestions] = useState<QuestionItem[]>([]);
    const [categories, setCategories] = useState<ReturnType<typeof flattenTree>>([]);
    const [search, setSearch] = useState('');
    const [filterLevel1, setFilterLevel1] = useState('');
    const [filterDifficulty, setFilterDifficulty] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [configLoading, setConfigLoading] = useState(true);
    const [searched, setSearched] = useState(false);
    const searchInputRef = useRef<HTMLInputElement>(null);

    const token = () => localStorage.getItem('accessToken') || '';

    // Carregar config atual
    const loadConfig = useCallback(async () => {
        setConfigLoading(true);
        try {
            const res = await fetch('/api/admin/free-config/question-config', {
                headers: { Authorization: `Bearer ${token()}` }
            });
            if (res.ok) {
                const data = await res.json();
                setConfig(data);
            }
        } catch {
            onShowToast('Erro ao carregar configuração', 'error');
        } finally {
            setConfigLoading(false);
        }
    }, [onShowToast]);  // ✅ manter — usa onShowToast no catch

    // Carregar categorias para o filtro
    const loadCategories = useCallback(async () => {
        try {
            const res = await fetch('/api/questions/categories/tree', {
                headers: { Authorization: `Bearer ${token()}` }
            });
            if (res.ok) {
                const data = await res.json();
                setCategories(flattenTree(data.categories || []));
            }
        } catch { }
    }, []);  // ✅ sem dependências — catch vazio não usa onShowToast

    useEffect(() => {
        loadConfig();
        loadCategories();
    }, [loadConfig, loadCategories]);

    // Buscar questões com filtros
    const handleSearch = useCallback(async () => {
        setLoading(true);
        setSearched(true);
        try {
            const params = new URLSearchParams();
            params.set('limit', '50');
            params.set('page', '1');
            if (search.trim()) params.set('search', search.trim());
            if (filterLevel1) params.set('level1Id', filterLevel1);
            if (filterDifficulty) params.set('difficulty', filterDifficulty);

            const res = await fetch(`/api/questions?${params.toString()}`, {
                headers: { Authorization: `Bearer ${token()}` }
            });
            if (res.ok) {
                const data = await res.json();
                setQuestions(data.questions || []);
            }
        } catch {
            onShowToast('Erro ao buscar questões', 'error');
        } finally {
            setLoading(false);
        }
    }, [search, filterLevel1, filterDifficulty, onShowToast]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') handleSearch();
    };

    const toggleQuestion = (id: string) => {
        setConfig(prev => ({
            ...prev,
            freeQuestionIds: prev.freeQuestionIds.includes(id)
                ? prev.freeQuestionIds.filter(q => q !== id)
                : [...prev.freeQuestionIds, id]
        }));
    };

    const toggleAll = () => {
        const allIds = questions.map(q => q._id);
        const allSelected = allIds.every(id => config.freeQuestionIds.includes(id));
        if (allSelected) {
            setConfig(prev => ({
                ...prev,
                freeQuestionIds: prev.freeQuestionIds.filter(id => !allIds.includes(id))
            }));
        } else {
            const merged = Array.from(new Set([...config.freeQuestionIds, ...allIds]));
            setConfig(prev => ({ ...prev, freeQuestionIds: merged }));
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await fetch('/api/admin/free-config/question-config', {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token()}`
                },
                body: JSON.stringify(config)
            });
            if (res.ok) {
                onShowToast('Configuração salva com sucesso', 'success');
            } else {
                onShowToast('Erro ao salvar', 'error');
            }
        } catch {
            onShowToast('Erro ao salvar', 'error');
        } finally {
            setSaving(false);
        }
    };

    const level1Options = categories.filter(c => c.level === 1);
    const selectedCount = config.freeQuestionIds.length;
    const visibleSelected = questions.filter(q => config.freeQuestionIds.includes(q._id)).length;
    const allVisibleSelected = questions.length > 0 && questions.every(q => config.freeQuestionIds.includes(q._id));

    const truncate = (text: string, max = 120) => {
        const clean = text.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
        return clean.length > max ? clean.slice(0, max) + '...' : clean;
    };

    if (configLoading) {
        return (
            <div className="fqa-loading">
                <RefreshCw size={24} className="fqa-spin" />
                <p>Carregando configuração...</p>
            </div>
        );
    }

    return (
        <div className="fqa-root">

            {/* ── SEÇÃO 1: Configuração global ── */}
            <div className="fqa-section">
                <div className="fqa-section-header">
                    <h3>Configuração Global</h3>
                    <p>Defina se questões gratuitas estão ativas e os limites de uso por usuário.</p>
                </div>

                <div className="fqa-config-grid">
                    {/* Toggle enabled */}
                    <div className="fqa-config-card">
                        <div className="fqa-config-card-left">
                            <span className="fqa-config-label">Questões gratuitas</span>
                            <span className="fqa-config-desc">
                                {config.enabled ? 'Ativo — usuários podem responder questões gratuitas' : 'Inativo — todas as questões requerem compra'}
                            </span>
                        </div>
                        <button
                            className={`fqa-toggle ${config.enabled ? 'on' : 'off'}`}
                            onClick={() => setConfig(p => ({ ...p, enabled: !p.enabled }))}
                        >
                            {config.enabled
                                ? <ToggleRight size={28} strokeWidth={1.8} />
                                : <ToggleLeft size={28} strokeWidth={1.8} />
                            }
                            <span>{config.enabled ? 'Ativo' : 'Inativo'}</span>
                        </button>
                    </div>

                    {/* Limite diário */}
                    <div className="fqa-config-card">
                        <div className="fqa-config-card-left">
                            <span className="fqa-config-label">Limite diário</span>
                            <span className="fqa-config-desc">
                                Máximo de questões que o usuário pode responder por dia. 0 = sem limite diário.
                            </span>
                        </div>
                        <div className="fqa-number-input-wrap">
                            <input
                                type="number"
                                className="fqa-number-input"
                                min={0}
                                value={config.dailyLimit}
                                onChange={e => setConfig(p => ({ ...p, dailyLimit: Number(e.target.value) }))}
                            />
                            <span className="fqa-number-suffix">/ dia</span>
                        </div>
                    </div>

                    {/* Limite total */}
                    <div className="fqa-config-card">
                        <div className="fqa-config-card-left">
                            <span className="fqa-config-label">Limite total</span>
                            <span className="fqa-config-desc">
                                Máximo de questões que o usuário pode responder no total (acumulado). 0 = sem limite total.
                            </span>
                        </div>
                        <div className="fqa-number-input-wrap">
                            <input
                                type="number"
                                className="fqa-number-input"
                                min={0}
                                value={config.totalLimit}
                                onChange={e => setConfig(p => ({ ...p, totalLimit: Number(e.target.value) }))}
                            />
                            <span className="fqa-number-suffix">total</span>
                        </div>
                    </div>
                </div>

                {/* Info badge */}
                <div className="fqa-info-badge">
                    <Info size={14} />
                    <span>
                        {selectedCount} questão{selectedCount !== 1 ? 'ões' : ''} selecionada{selectedCount !== 1 ? 's' : ''} como gratuita{selectedCount !== 1 ? 's' : ''}.
                        {config.dailyLimit > 0 && ` Limite de ${config.dailyLimit}/dia.`}
                        {config.totalLimit > 0 && ` Máximo de ${config.totalLimit} no total.`}
                    </span>
                </div>
            </div>

            {/* ── SEÇÃO 2: Busca de questões ── */}
            <div className="fqa-section">
                <div className="fqa-section-header">
                    <h3>Selecionar Questões Gratuitas</h3>
                    <p>Busque e marque as questões que ficarão disponíveis gratuitamente.</p>
                </div>

                <div className="fqa-search-bar">
                    <div className="fqa-search-input-wrap">
                        <Search size={16} className="fqa-search-icon" />
                        <input
                            ref={searchInputRef}
                            type="text"
                            className="fqa-search-input"
                            placeholder="Buscar por texto da questão..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            onKeyDown={handleKeyDown}
                        />
                        {search && (
                            <button className="fqa-search-clear" onClick={() => setSearch('')}>
                                <X size={14} />
                            </button>
                        )}
                    </div>

                    <div className="fqa-select-wrap">
                        <select
                            className="fqa-select"
                            value={filterLevel1}
                            onChange={e => setFilterLevel1(e.target.value)}
                        >
                            <option value="">Todos os campos</option>
                            {level1Options.map(c => (
                                <option key={c._id} value={c._id}>{c.name}</option>
                            ))}
                        </select>
                        <ChevronDown size={14} className="fqa-select-icon" />
                    </div>

                    <div className="fqa-select-wrap">
                        <select
                            className="fqa-select"
                            value={filterDifficulty}
                            onChange={e => setFilterDifficulty(e.target.value)}
                        >
                            <option value="">Todas as dificuldades</option>
                            <option value="facil">Fácil</option>
                            <option value="medio">Médio</option>
                            <option value="dificil">Difícil</option>
                        </select>
                        <ChevronDown size={14} className="fqa-select-icon" />
                    </div>

                    <button
                        className="fqa-btn-search"
                        onClick={handleSearch}
                        disabled={loading}
                    >
                        {loading ? <RefreshCw size={15} className="fqa-spin" /> : <Search size={15} />}
                        Buscar
                    </button>
                </div>

                {/* Lista de questões */}
                {!searched ? (
                    <div className="fqa-empty-state">
                        <Search size={36} strokeWidth={1.3} />
                        <p>Use a busca acima para encontrar questões</p>
                        <span>Filtre por campo de estudo, dificuldade ou busque pelo enunciado</span>
                    </div>
                ) : loading ? (
                    <div className="fqa-loading-list">
                        <RefreshCw size={20} className="fqa-spin" />
                        <span>Buscando questões...</span>
                    </div>
                ) : questions.length === 0 ? (
                    <div className="fqa-empty-state">
                        <Search size={36} strokeWidth={1.3} />
                        <p>Nenhuma questão encontrada</p>
                        <span>Tente outros termos ou filtros</span>
                    </div>
                ) : (
                    <>
                        {/* Barra de controle da lista */}
                        <div className="fqa-list-controls">
                            <button className="fqa-select-all-btn" onClick={toggleAll}>
                                {allVisibleSelected
                                    ? <CheckSquare size={15} />
                                    : <Square size={15} />
                                }
                                {allVisibleSelected ? 'Desmarcar todos' : 'Marcar todos'}
                            </button>
                            <span className="fqa-list-count">
                                {questions.length} questão{questions.length !== 1 ? 'ões' : ''} encontrada{questions.length !== 1 ? 's' : ''}
                                {visibleSelected > 0 && ` · ${visibleSelected} selecionada${visibleSelected !== 1 ? 's' : ''}`}
                            </span>
                        </div>

                        <div className="fqa-question-list">
                            {questions.map(q => {
                                const isSelected = config.freeQuestionIds.includes(q._id);
                                const catPath = [q.category?.level1, q.category?.level2]
                                    .filter(Boolean).join(' › ');

                                return (
                                    <div
                                        key={q._id}
                                        className={`fqa-question-row ${isSelected ? 'selected' : ''}`}
                                        onClick={() => toggleQuestion(q._id)}
                                    >
                                        <div className="fqa-question-check">
                                            {isSelected
                                                ? <CheckSquare size={18} className="fqa-check-on" />
                                                : <Square size={18} className="fqa-check-off" />
                                            }
                                        </div>

                                        <div className="fqa-question-body">
                                            <p className="fqa-question-text">
                                                {truncate(q.statement)}
                                            </p>
                                            {catPath && (
                                                <span className="fqa-question-cat">{catPath}</span>
                                            )}
                                        </div>

                                        <div className="fqa-question-meta">
                                            <span
                                                className="fqa-diff-badge"
                                                style={{ color: DIFF_COLOR[q.difficulty] }}
                                            >
                                                {DIFF_LABEL[q.difficulty]}
                                            </span>
                                            {isSelected && (
                                                <span className="fqa-free-chip">
                                                    Gratuita
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>

            {/* ── BOTÃO SALVAR FIXO ── */}
            <div className="fqa-save-bar">
                <div className="fqa-save-bar-info">
                    <Lock size={14} />
                    <span>{selectedCount} questão{selectedCount !== 1 ? 'ões' : ''} gratuita{selectedCount !== 1 ? 's' : ''} configurada{selectedCount !== 1 ? 's' : ''}</span>
                </div>
                <button
                    className="fqa-btn-save"
                    onClick={handleSave}
                    disabled={saving}
                >
                    {saving
                        ? <><RefreshCw size={15} className="fqa-spin" /> Salvando...</>
                        : <><Save size={15} /> Salvar configuração</>
                    }
                </button>
            </div>
        </div>
    );
};

export default FreeQuestionsAdmin;
